<template>
  <div>
    <div class="d-flex mb-4">
      <div class="basket-product__preview mr-3" @click="handleProductClick">
        <img
          v-if="isProductFormatDigital"
          class="basket-product__preview-digital-badge"
          src="@/assets/icons/pdf-badge.svg"
          alt="digital-format"
        />
        <img
          class="basket-product__preview-image"
          :src="getPreviewImage(product)"
          :alt="`preview-${product.id}`"
        />
      </div>
      <div class="basket-product__details">
        <div class="d-flex flex-column">
          <div class="basket-product__details-price mb-2">
            {{ productFormatPriceAmount }}
          </div>
          <p class="basket-product__details-title mb-1">{{ product.name }}</p>
          <div class="quantity d-flex align-items-center mt-2 mb-3">
            <span
              v-if="productFormats.length === 1"
              class="basket-product__quantity-text mr-2"
            >
              {{ productFormatName }}</span
            >
            <CustomSelector
              v-if="productFormats.length > 1"
              v-model="selectedProductFormat"
              class="counter-select"
              :options="productFormatOptions"
              :placeholder="productFormatName"
            />
          </div>
          <div
            v-if="!isProductFormatDigital"
            class="quantity d-flex align-items-center"
          >
            <CustomSelector
              v-model="selectedQuantity"
              class="counter-select"
              :options="defaultQuantityOptions"
              :placeholder="`Quantity: ${product.quantity}`"
            />
          </div>
        </div>

        <div class="d-flex justify-content-end align-items-end mb-lg-3">
          <img
            class="basket-product__delete-btn"
            src="@/assets/icons/trash.svg"
            alt="logo"
            @click="handleDeleteClick(index)"
          />
        </div>
      </div>
    </div>
    <Divider class="my-0" />
  </div>
</template>

<script>
import { isInteger, map } from 'lodash';
import { mapGetters, mapMutations, mapState } from 'vuex';

import CustomSelector from '@/components/BasketProduct/CustomSelector';
import Divider from '@/components/Elements/Divider';

import {
  FallbackImageURL,
  ProductFormatFulfilment,
  DefaultQuantityOptions,
  ProductFormatAvailability
} from '@/misc/constants';
import { fetchProductConfig } from '@/services/config';
import formatMoney from '@/misc/formatMoney';
import getFormatPrice from '@/misc/getFormatPrice';
import getProductFormatName from '@/misc/getProductFormatName';

export default {
  name: 'BasketProduct',

  components: {
    CustomSelector,
    Divider
  },

  props: {
    product: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      questions: [],
      defaultQuantityOptions: DefaultQuantityOptions
    };
  },

  computed: {
    ...mapState('config', ['config']),
    ...mapGetters({
      currency: 'config/currency',
      region: 'config/region'
    }),
    selectedProductFormat: {
      get() {
        const formats = this.$get(this.product, 'formats', []);
        const formatId = this.$get(this.product, 'productFormatId');

        return formats.find(({ _id }) => _id === formatId);
      },
      set(value) {
        this.CHANGE_ITEM_FORMAT({ index: this.index, value });
      }
    },
    productFormatName() {
      return getProductFormatName(this.selectedProductFormat);
    },
    productFormatPrice() {
      return getFormatPrice(this.selectedProductFormat, this.region);
    },
    productFormatPriceAmount() {
      return formatMoney(this.productFormatPrice.amount, this.currency);
    },
    isTaxLabelVisible() {
      return this.productFormatPrice.taxBehaviour === 'exclusive';
    },
    isValid() {
      return !!(
        isInteger(this.product.quantity) &&
        this.product.quantity &&
        this.product.quantity > 0 &&
        this.product.quantity < 100
      );
    },
    isProductFormatDigital() {
      return (
        this.selectedProductFormat?.fulfilment ===
        ProductFormatFulfilment.Digital
      );
    },
    selectedQuantity: {
      get() {
        return toString(this.product.quantity);
      },
      set(value) {
        this.CHANGE_ITEM_QUANTITY({ index: this.index, value: Number(value) });
      }
    },
    productFormats() {
      return (this.product?.formats || []).filter(
        ({ availability, prices, deletedAt }) =>
          !deletedAt &&
          availability === ProductFormatAvailability.Available &&
          prices.some((price) => price.region === this.region)
      );
    },
    productFormatOptions() {
      return map(this.productFormats, (format) => {
        const formatName = getProductFormatName(format);
        const formatPrice = getFormatPrice(format, this.region);
        const priceAmount = formatMoney(formatPrice.amount, this.currency);

        return {
          text: `${formatName} - ${priceAmount}`,
          value: format._id
        };
      });
    },
    isGarmentsProduct() {
      return (
        this.productFormats?.filter(
          ({ availability, deletedAt }) =>
            !deletedAt && availability === ProductFormatAvailability.Available
        ).length > 4
      );
    }
  },

  mounted() {
    fetchProductConfig(this.$axios, this.product?.id).then((response) => {
      this.questions = this.$get(response, 'questions', []);
    });
  },

  methods: {
    ...mapMutations({
      CHANGE_ITEM_QUANTITY: 'checkout/CHANGE_ITEM_QUANTITY',
      CHANGE_ITEM_FORMAT: 'checkout/CHANGE_ITEM_FORMAT',
      REMOVE_ITEM: 'checkout/REMOVE_ITEM',
      ADD_ITEM: 'checkout/ADD_ITEM'
    }),
    handleProductClick() {
      this.$emit('click');
    },
    getPreviewImage(product) {
      if (product?.previewImages && product?.previewImages?.length) {
        if (this.isGarmentsProduct) {
          // extract design name ("Design 1" from "Design 1 - L")
          const [designName] = this.productFormatName.split(' - ');

          if (!designName) return null;

          // find matching image by meta
          const image = product.previewImages.find(
            (img) => img.options?.design === designName
          );

          if (image) {
            return image.url;
          }
        }
        return product?.previewImages[0]?.url;
      }
      return FallbackImageURL;
    },
    onlyNumbers(value) {
      if (isInteger(+value) && value > 0 && value < 100) {
        return value;
      }
      return value?.substring(0, value.length - 1);
    },
    handleDeleteClick(index) {
      this.REMOVE_ITEM(index);
    }
  }
};
</script>

<style lang="scss" scoped>
.basket-product {
  &__preview {
    position: relative;

    &-image {
      cursor: pointer;
      width: 128px;
      height: 128px;
      border-radius: 5px;
    }

    &-digital-badge {
      position: absolute;
      width: 30px;
      right: 5px;
      top: 0;
    }
  }

  &__details {
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
    color: $dark-color;

    &-price {
      font-size: 20px;
      line-height: 20px;
      font-weight: $font-weight-regular;
    }

    &-title {
      font-size: 18px;
      line-height: 24px;
      font-weight: $font-weight-demi-bold;
      font-family: 'Poppins-Bold', sans-serif;
    }

    &-availability {
      font-size: 12px;
    }

    &-personalisation {
      max-width: 329px;
      width: 100%;
    }

    &-tax {
      font-size: $font-size-small;
      letter-spacing: 0;
    }

    .personalisation-panel {
      margin-bottom: 5px;
    }
  }

  &__quantity-text {
    margin-right: 1rem;
    line-height: 18px;
  }

  &__counter {
    max-width: 95px;
    margin-right: 1rem;
  }

  &__divider {
    height: 30px;
  }

  &__delete-btn {
    width: 20px;
    height: 24px;
    cursor: pointer;
  }
}

.quantity {
  height: 20px;
}

.quantity::v-deep {
  .invalid-feedback {
    max-width: 95px;
    line-height: 16px;
  }
}

@media (max-width: 768px) {
  .basket-product {
    &__details {
      flex-direction: column;

      .personalisation-panel {
        margin-top: 17px;
        margin-bottom: 5px;
      }

      &-personalisation {
        max-width: 260px;
      }
    }
  }
}

@media (max-width: $screen-width-md) {
  .basket-product__counter {
    max-width: 85px;
  }
}

@media (max-width: 410px) {
  .basket-product {
    &__quantity-text {
      margin-right: 0.3rem !important;
    }

    &__counter {
      margin-right: 0.3rem !important;
    }
    &__delete-btn {
      margin-left: 0.3rem;
    }
  }
}
</style>
